import type { ComponentPropsWithoutRef } from 'react';

import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import classes from './AgentMarkdown.module.css';
import { LocatedItemLink } from './LocatedItemLink';
import { isDisplayId, itemPath, needsLocation } from './itemLinks';

/** The part of a Markdown syntax tree node that the break plugin reads. */
type MarkdownNode = { type: string; value?: string; children?: MarkdownNode[] };

const HTML_BREAK = /^<br\s*\/?>$/i;

function replaceHtmlBreaks(node: MarkdownNode): void {
  node.children?.forEach((child, index, children) => {
    if (child.type === 'html' && HTML_BREAK.test(child.value ?? '')) {
      children[index] = { type: 'break' };
    } else {
      replaceHtmlBreaks(child);
    }
  });
}

/**
 * A model writes `<br>` to break a line inside a table cell, where Markdown
 * has no other way to do it. Raw HTML is never rendered, so this one tag
 * becomes a Markdown line break, and every other tag stays text.
 */
function remarkHtmlBreaks() {
  return replaceHtmlBreaks;
}

/**
 * A link in a reply. The agent links an item as `[name](DISPLAYID)`, and the
 * link opens the item's page in Teo, in the item's ownership where the page is
 * scoped to one. An item with no page shows its name only.
 * Any other link opens outside Teo, in a new tab.
 */
function AgentLink({ href, children }: ComponentPropsWithoutRef<'a'>) {
  if (href !== undefined && isDisplayId(href)) {
    if (needsLocation(href)) return <LocatedItemLink displayId={href}>{children}</LocatedItemLink>;
    const path = itemPath(href);
    return path === undefined ? (
      <>{children}</>
    ) : (
      <Link to={path} data-testid="agent-item-link">
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

/**
 * A reply of the agent, which the model writes in Markdown: emphasis, code,
 * lists, headings, tables and links to items. Raw HTML in a reply is shown
 * as text, never rendered.
 */
export function AgentMarkdown({ children }: { children: string }) {
  return (
    <div className={classes.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkHtmlBreaks]} components={{ a: AgentLink }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
